import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import styles from './PaymentHistory.module.css';

const STATUS_LABELS = {
  success: '成功',
  failed: '失败',
  refunded: '已退款',
};

const PAYMENT_METHODS = {
  card: { label: '银行卡', icon: 'credit_card', color: '#3b82f6' },
  alipay: { label: '支付宝', icon: 'account_balance_wallet', color: '#3b82f6' },
  wechat_pay: { label: '微信支付', icon: 'qr_code_2', color: '#10b981' },
};

const PAGE_SIZE = 10;

const PaymentHistory = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vipStatus, setVipStatus] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, statusRes] = await Promise.all([
        api.get(`/payment/my-records?offset=${offset}&limit=${PAGE_SIZE}`),
        api.get('/payment/check-vip-status'),
      ]);
      const list = recordsRes.data || [];
      setRecords(list);
      setHasMore(list.length >= PAGE_SIZE);
      setVipStatus(statusRes.data || null);
    } catch (err) {
      console.error('获取支付记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(isoStr));
  };

  const formatShortDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric', month: 'long', day: 'numeric',
    }).format(new Date(isoStr));
  };

  const getPaymentMethod = (method) => {
    return PAYMENT_METHODS[method] || { label: method || '-', icon: 'payments', color: '#94a3b8' };
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className={styles.title}>支付记录</h1>
      </header>

      <div className={styles.content}>
        {/* VIP 状态卡片 */}
        {vipStatus && (
          <div className={`${styles.statusCard} ${vipStatus.is_vip ? styles.statusCardVip : styles.statusCardNormal}`}>
            <div className={styles.statusCardBg} />
            <div className={styles.statusInner}>
              <div className={styles.statusLeft}>
                <div className={styles.statusIcon}>
                  <span className="material-symbols-outlined">
                    {vipStatus.is_vip ? 'verified' : 'stars'}
                  </span>
                </div>
                <div>
                  <div className={styles.statusTitleRow}>
                    <h2 className={styles.statusTitle}>
                      {vipStatus.is_vip ? 'VIP 会员' : '非 VIP'}
                    </h2>
                    {vipStatus.is_vip && (
                      <span className={styles.proBadge}>Pro</span>
                    )}
                  </div>
                  {vipStatus.is_vip && vipStatus.vip_expires_at && (
                    <p className={styles.statusDesc}>
                      有效期至 {formatShortDate(vipStatus.vip_expires_at)}
                      {vipStatus.days_remaining != null && ` (剩余 ${vipStatus.days_remaining} 天)`}
                    </p>
                  )}
                  {!vipStatus.is_vip && (
                    <p className={styles.statusDesc}>开通 VIP 解锁更多功能</p>
                  )}
                </div>
              </div>
              <button
                className={styles.renewBtn}
                onClick={() => navigate('/vip/renewal')}
              >
                {vipStatus.is_vip ? '立即续费' : '立即开通'}
              </button>
            </div>
          </div>
        )}

        {/* 交易历史 */}
        <section className={styles.historySection}>
          <div className={styles.historyHeader}>
            <h3 className={styles.historyTitle}>交易历史</h3>
          </div>

          {loading ? (
            <div className={styles.loadingWrap}>
              <p className={styles.hint}>加载中…</p>
            </div>
          ) : records.length === 0 ? (
            <div className={styles.emptyWrap}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.25 }}>receipt_long</span>
              <p>暂无支付记录</p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>交易名称</th>
                      <th className={styles.thCenter}>金额</th>
                      <th>支付方式</th>
                      <th>状态</th>
                      <th className={styles.thRight}>时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className={styles.row}>
                        <td>
                          <div className={styles.cellName}>
                            <span className={styles.planName}>{r.plan_name}</span>
                            {r.vip_expires_at && (
                              <span className={styles.cellSub}>VIP 到期: {formatShortDate(r.vip_expires_at)}</span>
                            )}
                          </div>
                        </td>
                        <td className={styles.tdCenter}>
                          {r.amount && r.amount !== '0' ? (
                            <span className={styles.amount}>
                              {r.currency === 'CNY' ? '¥' : (r.currency || '')}{r.amount}
                            </span>
                          ) : (
                            <span className={styles.amountNone}>-</span>
                          )}
                        </td>
                        <td>
                          <div className={styles.methodCell}>
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: 20, color: getPaymentMethod(r.payment_method).color }}
                            >
                              {getPaymentMethod(r.payment_method).icon}
                            </span>
                            <span className={styles.methodLabel}>
                              {getPaymentMethod(r.payment_method).label}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[`status_${r.status}`]}`}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td className={styles.tdRight}>
                          <span className={styles.timeText}>{formatDate(r.created_at)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 分页 */}
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>第 {currentPage} 页</span>
                <div className={styles.pageActions}>
                  <button
                    className={styles.pageBtn}
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    上一页
                  </button>
                  <button
                    className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
                    disabled={!hasMore}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    下一页
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PaymentHistory;
